import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import type { VariantProps } from "class-variance-authority"
import { cva } from "class-variance-authority"
import {
  ChevronDown,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { useUserPreferences } from "@/providers/UserPreferencesProvider"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const SIDEBAR_COOKIE_NAME = "sidebar_state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_MOBILE = "18rem"
// Collapsed icon widths per variant.
const SIDEBAR_WIDTH_ICON_DOCKED = "3rem"
const SIDEBAR_WIDTH_ICON_FLOATING = "4.1rem"
// Backward compat (kept for any external usage – defaults to docked width)
const SIDEBAR_WIDTH_ICON = SIDEBAR_WIDTH_ICON_DOCKED
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

type SidebarContextProps = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  side: "left" | "right"
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  side = "left",
  ...props
}: Readonly<React.ComponentProps<"div">> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  side?: "left" | "right"
}) {
  const isMobile = useIsMobile()
  const { preferences } = useUserPreferences()
  const [openMobile, setOpenMobile] = React.useState(false)

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    },
    [setOpenProp, open]
  )

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open)
  }, [isMobile, setOpen, setOpenMobile])

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    // Only add keyboard shortcut if enabled in preferences
    if (!preferences.keyboardShortcutsEnabled) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggleSidebar, preferences.keyboardShortcutsEnabled])

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed"

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      side,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar, side]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon-docked": SIDEBAR_WIDTH_ICON_DOCKED,
              "--sidebar-width-icon-floating": SIDEBAR_WIDTH_ICON_FLOATING,
              // legacy var (maps to docked width)
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn("group/sidebar-wrapper flex h-full w-full", className)}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  )
}

function Sidebar({
  side = "left",
  variant = "docked",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: Readonly<React.ComponentProps<"div">> & {
  side?: "left" | "right"
  variant?: "docked" | "floating"
  collapsible?: "offcanvas" | "icon" | "none"
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-[var(--sidebar-width)] flex-col",
          className
        )}
        data-variant={variant}
        data-side={side}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="bg-sidebar text-sidebar-foreground w-[var(--sidebar-width)] p-0 [&>button]:hidden"
          style={
            { "--sidebar-width": SIDEBAR_WIDTH_MOBILE } as React.CSSProperties
          }
          side={side}
          data-variant={variant}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <div
      className="group peer text-sidebar-foreground hidden md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative w-[var(--sidebar-width)] bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          // Variant-specific collapsed gap widths
          "group-data-[variant=floating]:group-data-[collapsible=icon]:w-[var(--sidebar-width-icon-floating)]",
          "group-data-[variant=docked]:group-data-[collapsible=icon]:w-[var(--sidebar-width-icon-docked)]"
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed top-0 bottom-0 z-10 hidden h-screen w-[var(--sidebar-width)] transition-[left,right,width] duration-200 ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Floating variant hooks
          "group-data-[variant=floating]:p-2 group-data-[variant=floating]:group-data-[collapsible=icon]:w-[var(--sidebar-width-icon-floating)]",
          // Docked variant hooks
          "group-data-[variant=docked]:group-data-[collapsible=icon]:w-[var(--sidebar-width-icon-docked)]",
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className={cn(
            "bg-sidebar flex h-full  w-full flex-col",
            // Floating variant styling surface
            "group-data-[variant=floating]:border-neutral-200 group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm",
            // Docked variant styling surface
            " group-data-[variant=docked]:rounded-0 group-data-[variant=floating]:rounded-lg border-r"
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: Readonly<React.ComponentProps<typeof Button>>) {
  const { toggleSidebar, isMobile, open, openMobile, side } = useSidebar()
  // Use openMobile for mobile, open for desktop
  const isOpen = isMobile ? openMobile : open
  
  // Determine icon based on sidebar position and open state
  // For left sidebar: open = PanelLeftClose, closed = PanelLeftOpen
  // For right sidebar: open = PanelLeftOpen (chevron points right), closed = PanelLeftClose (chevron points left)
  const Icon = side === "left" 
    ? (isOpen ? PanelLeftClose : PanelLeftOpen)
    : (isOpen ? PanelLeftOpen : PanelLeftClose)

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn(
        "size-8",
        "group-data-[state=collapsed]:h-8 group-data-[state=collapsed]:w-8",
        className
      )}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <Icon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

function SidebarRail({
  className,
  ...props
}: Readonly<React.ComponentProps<"button">>) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarInset({
  className,
  ...props
}: Readonly<React.ComponentProps<"main">>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "bg-background relative flex w-full flex-1 flex-col",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarInput({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof Input>>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn("bg-background h-8 w-full shadow-none", className)}
      {...props}
    />
  )
}

function SidebarHeader({
  className,
  ...props
}: Readonly<React.ComponentProps<"div">>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        "flex items-center w-full h-12 px-2 py-2 shrink-0",
        "group-data-[state=collapsed]:[&>div:first-child]:w-8 group-data-[state=collapsed]:[&>div:first-child]:min-w-8",
        className
      )}
      {...props}
    />
  )
}

function SidebarFooter({
  className,
  ...props
}: Readonly<React.ComponentProps<"div">>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

function SidebarSeparator({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof Separator>>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("bg-sidebar-border mx-2 w-auto", className)}
      {...props}
    />
  )
}

function SidebarContent({
  className,
  ...props
}: Readonly<React.ComponentProps<"div">>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroup({
  className,
  ...props
}: Readonly<React.ComponentProps<"div">>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        "relative flex w-full min-w-0 flex-col items-center p-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  asChild = false,
  hideOnCollapse = true,
  ...props
}: Readonly<React.ComponentProps<"div">> & {
  asChild?: boolean
  hideOnCollapse?: boolean
}) {
  const Comp = asChild ? Slot : "div"

  const collapseHideClasses = hideOnCollapse
    ? "group-data-[collapsible=icon]:-mt-2 group-data-[collapsible=icon]:opacity-0 group-data-[state=collapsed]:hidden"
    : ""

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "text-neutral-800 w-full ring-sidebar-ring flex h-8 shrink-0 p-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        collapseHideClasses,
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: Readonly<React.ComponentProps<"button">> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "text-neutral-800 ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupContent({
  className,
  ...props
}: Readonly<React.ComponentProps<"div">>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  )
}

function SidebarMenu({
  className,
  ...props
}: Readonly<React.ComponentProps<"ul">>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-", className)}
      {...props}
    />
  )
}

function SidebarMenuItem({
  className,
  ...props
}: Readonly<React.ComponentProps<"li">>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative", className)}
      {...props}
    />
  )
}
const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground text-[state=closed]:none data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! group-data-[collapsible=icon]:w-8 [&>span:last-child]:truncate group-data-[state=collapsed]:[&>span:last-child]:hidden [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary/00 hover:bg-teal-100 text-teal-600 hover:text-teal-700 dark:hover:bg-primary/10",
        outline:
          "bg-background shadow-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-sm",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// Added back dropdownMenuItemVariants
const dropdownMenuItemVariants = cva(
  "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 data-[inset]:pl-8",
  {
    variants: {
      size: {
        sm: "px-2 py-1.5 text-sm",
        md: "px-2.5 py-2 text-md",
        lg: "px-3 py-2.5 text-base",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  }
)

// Added missing interface for dropdown menu item props
interface SidebarDropdownMenuItemProps
  extends React.ComponentProps<typeof DropdownMenuItem>,
    VariantProps<typeof dropdownMenuItemVariants> {}

// Define menu button props (previously missing)
interface SidebarMenuButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof sidebarMenuButtonVariants> {
  asChild?: boolean
  isActive?: boolean
  tooltip?: string | React.ComponentProps<typeof TooltipContent>
}

const collapsibleTriggerVariants = cva(
  "flex w-full items-center gap-2 overflow-hidden rounded-md p-1 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! group-data-[collapsible=icon]:w-8 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-sm",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

interface SidebarCollapsibleTriggerProps
  extends React.ComponentProps<typeof CollapsibleTrigger>,
    VariantProps<typeof collapsibleTriggerVariants> {
  showIndicator?: boolean
}

const SidebarMenuButton = React.forwardRef<
  React.ComponentRef<"button">,
  SidebarMenuButtonProps
>(
  (
    {
      asChild = false,
      isActive = false,
      className,
      variant = "default",
      size = "default",
      tooltip,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    const { state } = useSidebar()
    const showTooltip = !!tooltip && state === "collapsed"

    const button = (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-button"
        data-sidebar="menu-button"
        data-state={state}
        data-active={isActive}
        className={cn(
          sidebarMenuButtonVariants({ variant, size }),
          "group-data-[collapsible=icon]:justify-center",
          className
        )}
        {...props}
      />
    )

    if (!showTooltip) return button

    const tooltipContent =
      typeof tooltip === "string" ? (
        <TooltipContent side="right">{tooltip}</TooltipContent>
      ) : (
        <TooltipContent side="right" {...tooltip} />
      )

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        {tooltipContent}
      </Tooltip>
    )
  }
)
SidebarMenuButton.displayName = "SidebarMenuButton"

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button">
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    data-slot="sidebar-menu-action"
    data-sidebar="menu-action"
    className={cn(
      "text-sidebar-foreground/70 hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/60 absolute top-1 right-2 flex size-6 p-1 items-center justify-center rounded-md outline-hidden ring-sidebar-ring transition-colors focus-visible:ring-2 group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
))
SidebarMenuAction.displayName = "SidebarMenuAction"

const SidebarMenuBadge = React.forwardRef<
  React.ComponentRef<"span">,
  React.ComponentProps<"span">
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="sidebar-menu-badge"
    data-sidebar="menu-badge"
    className={cn(
      "bg-sidebar-accent text-sidebar-accent-foreground ml-auto inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-none group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
))
SidebarMenuBadge.displayName = "SidebarMenuBadge"

const SidebarMenuSkeleton = ({ className }: { className?: string }) => (
  <Skeleton
    data-slot="sidebar-menu-skeleton"
    data-sidebar="menu-skeleton"
    className={cn(
      "h-8 w-full rounded-md group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8",
      className
    )}
  />
)
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton"

function SidebarMenuSub({
  className,
  ...props
}: Readonly<React.ComponentProps<"ul">>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        " border-l mt-1 ml-4 pl-2 flex flex-col gap-1 relative group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({
  className,
  ...props
}: Readonly<React.ComponentProps<"li">>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("relative", className)}
      {...props}
    />
  )
}

interface SidebarMenuSubButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof sidebarMenuButtonVariants> {
  asChild?: boolean
}

const SidebarMenuSubButton = React.forwardRef<
  React.ComponentRef<"button">,
  SidebarMenuSubButtonProps
>(
  (
    { asChild = false, className, variant = "default", size = "sm", ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-sub-button"
        data-sidebar="menu-sub-button"
        className={cn(
          sidebarMenuButtonVariants({ variant, size }),
          "pl-2 h-8 w-full text-sm pr-2",
          className
        )}
        {...props}
      />
    )
  }
)
SidebarMenuSubButton.displayName = "SidebarMenuSubButton"

const SidebarCollapsibleTrigger = React.forwardRef<
  React.ComponentRef<"button">,
  SidebarCollapsibleTriggerProps
>(
  (
    {
      className,
      children,
      showIndicator = true,
      variant = "default",
      size = "default",
      ...props
    },
    ref
  ) => (
    <CollapsibleTrigger
      ref={ref}
      className={cn(collapsibleTriggerVariants({ variant, size }), className)}
      {...props}
    >
      {children}
      {showIndicator && (
        <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
      )}
    </CollapsibleTrigger>
  )
)
SidebarCollapsibleTrigger.displayName = "SidebarCollapsibleTrigger"

const SidebarDropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuItem>,
  SidebarDropdownMenuItemProps
>(({ className, size = "sm", ...props }, ref) => (
  <DropdownMenuItem
    ref={ref}
    className={cn(dropdownMenuItemVariants({ size }), className)}
    {...props}
  />
))
SidebarDropdownMenuItem.displayName = "SidebarDropdownMenuItem"

function SidebarMenuItemswithMenu({
  icon: Icon,
  label,
  menuItems = [],
  tooltip,
  isActive = false,
  size = "default",
  variant = "default",
  className,
  menuItemSize = "sm",
  ...props
}: {
  icon?: LucideIcon
  label: string
  menuItems?: { label: string; onSelect?: () => void }[]
  tooltip?: string | React.ComponentProps<typeof TooltipContent>
  isActive?: boolean
  size?: VariantProps<typeof sidebarMenuButtonVariants>["size"]
  variant?: VariantProps<typeof sidebarMenuButtonVariants>["variant"]
  menuItemSize?: VariantProps<typeof dropdownMenuItemVariants>["size"]
} & Omit<React.ComponentProps<"button">, "children">) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={tooltip ?? label}
        data-active={isActive}
        size={size}
        variant={variant}
        className={className}
        {...props}
      >
        {Icon && <Icon />}
        <span>{label}</span>
      </SidebarMenuButton>
      {menuItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuAction
              aria-label="Open item menu"
              title="Open item menu"
              data-slot="sidebar-menu-inline-menu-trigger"
            >
              <MoreHorizontal />
              <span className="sr-only">Open item menu</span>
            </SidebarMenuAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right" className="w-40">
            {menuItems.map((mi) => (
              <SidebarDropdownMenuItem
                key={mi.label}
                size={menuItemSize}
                onSelect={mi.onSelect}
                className="cursor-pointer"
              >
                {mi.label}
              </SidebarDropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </SidebarMenuItem>
  )
}

// SidebarUser component
interface SidebarUserProps extends Readonly<React.ComponentProps<"div">> {
  readonly name: string
  readonly email?: string
  readonly avatarSrc?: string
  readonly avatarFallback?: string
  readonly forceVisibleOnCollapse?: boolean
  readonly hideTextOnCollapse?: boolean
  readonly imageAlt?: string
}

function SidebarUser({
  name,
  email,
  avatarSrc,
  avatarFallback,
  forceVisibleOnCollapse = false,
  hideTextOnCollapse = false,
  className,
  imageAlt = name,
  ...props
}: SidebarUserProps) {
  // const { state } = useSidebar() // not currently needed directly
  return (
    <div
      data-slot="sidebar-user"
      data-sidebar="user"
      className={cn(
        "flex w-full items-center gap-2 rounded-md p-2 text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        !forceVisibleOnCollapse && "group-data-[state=collapsed]:hidden",
        className
      )}
      {...props}
    >
      <Avatar
        className={cn(
          "h-8 w-8 shrink-0",
          hideTextOnCollapse &&
            "group-data-[state=collapsed]:h-8 group-data-[state=collapsed]:w-8"
        )}
      >
        {avatarSrc && <AvatarImage src={avatarSrc} alt={imageAlt} />}
        <AvatarFallback>{avatarFallback ?? name?.charAt(0)}</AvatarFallback>
      </Avatar>
      <div
        className={cn(
          "flex min-w-0 flex-col",
          hideTextOnCollapse && "group-data-[state=collapsed]:hidden"
        )}
      >
        <span className="truncate font-medium leading-tight">{name}</span>
        {email && (
          <span className="truncate text-xs text-muted-foreground leading-tight">
            {email}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * A trigger button for toggling the sidebar, rendered outside the sidebar area on mobile only.
 */
function SidebarMobileTrigger({
  className,
  onClick,
  ...props
}: Readonly<React.ComponentProps<typeof Button>>) {
  const { toggleSidebar, state, isMobile } = useSidebar()
  if (!isMobile) return null
  const Icon = state === "collapsed" ? PanelLeftOpen : PanelLeftClose
  return (
    <Button
      data-sidebar="mobile-trigger"
      data-slot="sidebar-mobile-trigger"
      variant="ghost"
      size="icon"
      className={cn(
        "fixed top-4 left-4 z-50 size-8 shadow-md bg-background border border-neutral-200",
        className
      )}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <Icon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarInput,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
  SidebarCollapsibleTrigger,
  SidebarMenuItemswithMenu,
  SidebarDropdownMenuItem,
  SidebarUser,
  SidebarMobileTrigger,
}
